import { v4 as uuid4 } from 'uuid';
import { APP_TO_WEB, WEB_TO_APP, sendApplicationEventByBridge } from '@constant/common/bridge-event';

interface ISaveFileResult {
  eventId: string;
  status: 'success' | 'cancelled' | 'error' | 'busy';
  destination?: 'downloads' | 'share';
  error?: string;
}

const requestFileEvent = (
  type: string,
  resultEvent: string,
  data: Record<string, unknown>,
  timeout = 30000,
): Promise<ISaveFileResult> => {
  const eventId = uuid4();
  return new Promise((resolve, reject) => {
    const cleanup = () => {
      clearTimeout(timer);
      window.removeEventListener(resultEvent, handleResult);
    };
    const handleResult = (event: Event) => {
      const result = (event as CustomEvent<ISaveFileResult>).detail;
      if (result?.eventId !== eventId) return;
      cleanup();
      resolve(result);
    };
    const timer = window.setTimeout(() => {
      cleanup();
      reject(new Error('앱의 응답이 없습니다. 다시 시도해주세요.'));
    }, timeout);
    window.addEventListener(resultEvent, handleResult);
    try {
      sendApplicationEventByBridge(type, { ...data, eventId });
    } catch (error) {
      cleanup();
      reject(error);
    }
  });
};

// 마지막 조각에서는 사용자가 iOS 파일 저장·공유를 마칠 때까지 기다린다.
const sendFileChunk = (data: Record<string, unknown>) =>
  requestFileEvent(
    WEB_TO_APP.SAVE_FILE,
    APP_TO_WEB.SAVE_FILE_RESULT,
    data,
    typeof data.totalChunk === 'number' && data.chunkIndex === data.totalChunk - 1 ? 600000 : 30000,
  );

export const openNativeDownload = async () => {
  if (!window.ReactNativeWebView) throw new Error('앱에서 사용할 수 있는 기능입니다.');
  const result = await requestFileEvent(WEB_TO_APP.OPEN_DOWNLOAD, APP_TO_WEB.OPEN_DOWNLOAD_RESULT, {});
  if (result.status !== 'success') throw new Error(result.error || '다운로드 화면을 열지 못했습니다.');
};

const readBase64 = (blob: Blob): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result).split(',')[1] ?? '');
    reader.onerror = () => reject(new Error('내보낼 파일을 읽지 못했습니다.'));
    reader.readAsDataURL(blob);
  });

export const saveNativeFile = async (blob: Blob, filename: string) => {
  if (!window.ReactNativeWebView) throw new Error('앱에서 사용할 수 있는 파일 저장 기능입니다.');
  const fileId = uuid4();
  const chunkSize = 512 * 1024;
  const chunkCount = Math.max(1, Math.ceil(blob.size / chunkSize));
  try {
    for (let chunkIndex = 0; chunkIndex < chunkCount; chunkIndex += 1) {
      const base64 = await readBase64(blob.slice(chunkIndex * chunkSize, (chunkIndex + 1) * chunkSize));
      const result = await sendFileChunk({
        fileId,
        filename,
        chunkIndex,
        base64,
        totalChunk: chunkCount,
      });
      if (result.status === 'cancelled') return;
      if (result.status !== 'success') throw new Error(result.error || '파일을 저장하지 못했습니다.');
      if (result.destination) return result;
    }
  } catch (error) {
    void sendFileChunk({ fileId, cancel: true }).catch(() => {});
    throw error;
  }
};
